/**
 * GaussianSplatPanel.js
 *
 * Left-toolbar panel that lists all Gaussian Splat captures saved for the
 * currently loaded scene. Each row has:
 *   • A label (waypoint name or custom label)
 *   • Created date
 *   • 👁 Eye button — toggle the splat on/off in the Cesium scene
 *   • 🗑 Remove button — remove from view (does not delete from backend)
 *
 * This panel is injected into the twinworld-app shadow DOM under
 * `#gaussian-splat-panel` by Scenes.js, and toggled via menu.js reducer.
 */

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { getBaseUrl, getCookie } from '../../envUtils';
import { parseTwinupCookie, formatBearerToken } from '../SceneTaskExplorer/auth_utils';
import Cookies from 'js-cookie';

// ── Tiny inline styles (no MUI needed — panel lives in shadow DOM) ────────────
const PANEL_STYLE = {
    position: 'fixed',
    top: '80px',
    left: '72px',
    width: '300px',
    maxHeight: 'calc(100vh - 100px)',
    background: 'rgba(30,30,30,0.97)',
    borderRadius: '10px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
    color: '#fff',
    fontFamily: 'Inter, sans-serif',
    fontSize: '13px',
    zIndex: 2000,
    overflowY: 'auto',
    padding: '0 0 8px 0',
};

const HEADER_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    position: 'sticky',
    top: 0,
    background: 'rgba(30,30,30,0.97)',
    zIndex: 1,
};

const ROW_STYLE = {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    gap: '8px',
};

const BTN_STYLE = {
    background: 'rgba(255,255,255,0.08)',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    padding: '4px 8px',
    fontSize: '15px',
    lineHeight: 1,
    flexShrink: 0,
};

const ACTIVE_BTN_STYLE = {
    ...BTN_STYLE,
    background: 'rgba(243,111,75,0.5)',
};

const EMPTY_STYLE = {
    padding: '24px 16px',
    color: 'rgba(255,255,255,0.45)',
    textAlign: 'center',
    lineHeight: 1.6,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const getAuthHeaders = () => {
    try {
        const cookieData = parseTwinupCookie(Cookies.get(getCookie()));
        const token = cookieData?.access_token;
        return token ? { Authorization: formatBearerToken(token) } : {};
    } catch {
        return {};
    }
};

const formatDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
        return iso;
    }
};

// ── Main Component ────────────────────────────────────────────────────────────

const GaussianSplatPanel = ({ onClose }) => {
    const [splats, setSplats] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [sceneLoaded, setSceneLoaded] = React.useState(!!window.sceneId);
    // Map of job_id → boolean (is this splat currently visible in scene)
    const [activeMap, setActiveMap] = React.useState({});

    // Fetch splat list whenever the scene changes
    React.useEffect(() => {
        const fetchSplats = async () => {
            const sceneId = window.sceneId;

            // No scene loaded — show prompt, don't hit the API
            if (!sceneId) {
                setSceneLoaded(false);
                setLoading(false);
                setSplats([]);
                setError(null);
                return;
            }

            setSceneLoaded(true);
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(
                    `${getBaseUrl()}/gaussian-splatting/splats/scene/${sceneId}`,
                    { credentials: 'include', headers: getAuthHeaders() }
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                setSplats(data.splats || []);
            } catch (err) {
                setError(`Failed to load splats: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchSplats();

        // Re-fetch when a new scene is loaded
        const handler = () => fetchSplats();
        window.addEventListener('twinworld:scene-loaded', handler);
        return () => window.removeEventListener('twinworld:scene-loaded', handler);
    }, []);

    const toggleSplat = async (splat) => {
        const jobId = splat.job_id;
        const isActive = !!activeMap[jobId];

        if (isActive) {
            // Remove from scene
            if (typeof window.removeGaussianSplat === 'function') {
                window.removeGaussianSplat(jobId);
            }
            setActiveMap((prev) => ({ ...prev, [jobId]: false }));
        } else {
            // Load into scene — use scene center lat/lon as anchor
            const lat = window.destSelectedCenter
                ? (window.destSelectedCenter.latitude * 180) / Math.PI
                : undefined;
            const lon = window.destSelectedCenter
                ? (window.destSelectedCenter.longitude * 180) / Math.PI
                : undefined;

            setActiveMap((prev) => ({ ...prev, [jobId]: 'loading' }));
            const ok = await (window.loadGaussianSplat?.(jobId, { lat, lon }) ?? Promise.resolve(false));
            setActiveMap((prev) => ({ ...prev, [jobId]: ok ? true : false }));
        }
    };

    return (
        <div style={PANEL_STYLE}>
            {/* ── Header ── */}
            <div style={HEADER_STYLE}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Splat icon */}
                    <span style={{ fontSize: '18px' }}>🔵</span>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>Gaussian Splats</span>
                </div>
                <button style={{ ...BTN_STYLE, fontSize: '16px' }} onClick={onClose} title="Close">✕</button>
            </div>

            {/* ── Body ── */}
            {/* No scene loaded */}
            {!sceneLoaded && (
                <div style={EMPTY_STYLE}>
                    <div style={{ fontSize: '28px', marginBottom: '10px' }}>🗺️</div>
                    <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#fff' }}>
                        No scene loaded
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.55, lineHeight: 1.6 }}>
                        Open a scene from the task list first,<br />
                        then Gaussian Splats for that scene<br />
                        will appear here.
                    </div>
                </div>
            )}
            {sceneLoaded && loading && (
                <div style={EMPTY_STYLE}>Loading splats…</div>
            )}
            {sceneLoaded && !loading && error && (
                <div style={{ ...EMPTY_STYLE, color: '#f36f4b' }}>{error}</div>
            )}
            {sceneLoaded && !loading && !error && splats.length === 0 && (
                <div style={EMPTY_STYLE}>
                    No Gaussian Splats captured for this scene yet.<br />
                    <span style={{ fontSize: '12px', opacity: 0.6 }}>
                        Submit images via the robot scanner to create one.
                    </span>
                </div>
            )}
            {sceneLoaded && !loading && !error && splats.map((splat) => {
                const isActive = activeMap[splat.job_id];
                const isLoading = isActive === 'loading';
                return (
                    <div key={splat.job_id} style={ROW_STYLE}>
                        {/* Label + date */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {splat.label}
                            </div>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>
                                {formatDate(splat.completed_at || splat.created_at)}
                            </div>
                        </div>

                        {/* Toggle visibility */}
                        <button
                            style={isActive === true ? ACTIVE_BTN_STYLE : BTN_STYLE}
                            title={isActive === true ? 'Hide splat' : 'Show splat in scene'}
                            onClick={() => !isLoading && sceneLoaded && toggleSplat(splat)}
                            disabled={isLoading || !sceneLoaded}
                        >
                            {isLoading ? '⏳' : isActive === true ? '👁' : '👁'}
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export default GaussianSplatPanel;

// ── Shadow DOM injection helper ───────────────────────────────────────────────

let _panelRoot = null;
let _panelContainer = null;

/**
 * Mount the GaussianSplatPanel into the twinworld-app shadow DOM.
 * Called once from Scenes.js useEffect on mount.
 */
export const mountGaussianSplatPanel = () => {
    const shadowRoot = document.querySelector('twinworld-app')?.shadowRoot;
    if (!shadowRoot) return;

    // Avoid duplicate mounts
    if (shadowRoot.querySelector('#gaussian-splat-panel')) return;

    _panelContainer = document.createElement('div');
    _panelContainer.id = 'gaussian-splat-panel';
    _panelContainer.style.display = 'none'; // hidden until toolbar button is clicked
    shadowRoot.appendChild(_panelContainer);

    const handleClose = () => {
        _panelContainer.style.display = 'none';
    };

    _panelRoot = createRoot(_panelContainer);
    _panelRoot.render(<GaussianSplatPanel onClose={handleClose} />);
};

/**
 * Toggle the panel open/closed.
 * Called by menu.js reducer when the 'gaussianSplats' toolbar item is clicked.
 */
export const toggleGaussianSplatPanel = () => {
    const shadowRoot = document.querySelector('twinworld-app')?.shadowRoot;
    if (!shadowRoot) return;
    const panel = shadowRoot.querySelector('#gaussian-splat-panel');
    if (!panel) return;
    panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
};

/**
 * Unmount the panel — called on Scenes.js cleanup.
 */
export const unmountGaussianSplatPanel = () => {
    if (_panelRoot) {
        _panelRoot.unmount();
        _panelRoot = null;
    }
    if (_panelContainer) {
        _panelContainer.remove();
        _panelContainer = null;
    }
};
